// utils/analytics.js
// Утилиты для работы с Google Analytics

/**
 * Получает Google Analytics client ID.
 * Приоритет:
 * 1. gtag API (GA4) - рекомендуемый способ, используется NEXT_PUBLIC_GA4_MEASUREMENT_ID.
 * 2. Прямое чтение специфичного для GA4 cookie (_ga_YOUR_MEASUREMENT_ID_SUFFIX) - фолбэк.
 * 3. Прямое чтение общего cookie _ga - фолбэк.
 * 4. Universal Analytics API (устаревший фолбэк, если все еще используется).
 *
 * @param {number} timeout - Таймаут в мс для ожидания ответа от API.
 * @returns {Promise<string|null>} - Google Analytics client ID или null, если не удалось получить.
 */
export const getGAClientId = (timeout = 2000) => {
  return new Promise((resolve) => {
    console.log("Attempt to get GA Client ID");

    let resolved = false; // Флаг, чтобы избежать многократного разрешения промиса

    const resolveOnce = (clientId) => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeoutId); // Очищаем общий таймаут
        if (clientId) {
            console.log("GA Client ID resolved to:", clientId);
        } else {
            console.log("GA Client ID resolved to null (not found or timeout).");
        }
        resolve(clientId);
      }
    };

    const timeoutId = setTimeout(() => {
      console.log("GA Client ID general timeout reached.");
      resolveOnce(null); // Разрешаем с null, если ничего не найдено по таймауту
    }, timeout);

    // 1. Попытка через gtag API (предпочтительный метод для GA4)
    if (typeof window !== 'undefined' && window.gtag && process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID) {
      try {
        console.log("Trying gtag API with Measurement ID:", process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID);
        window.gtag('get', process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID, 'client_id', (clientId) => {
          if (clientId && typeof clientId === 'string') {
            console.log("Successfully got client_id from gtag API:", clientId);
            resolveOnce(clientId);
          } else {
            console.log("gtag API returned null or invalid client_id. Will try cookie fallbacks.");
            // Не вызываем resolveOnce(null) здесь, даем шанс другим методам
          }
        });
      } catch (e) {
        console.error("Error using gtag API:", e);
        // Продолжаем, чтобы другие методы могли быть опробованы
      }
    } else {
      console.log("gtag API or NEXT_PUBLIC_GA4_MEASUREMENT_ID not available for gtag('get'). Will try cookie fallbacks.");
    }

    // Фолбэки на чтение cookie и UA API, если gtag API не вернул ID быстро.
    // Даем небольшой шанс gtag API (около 100мс), прежде чем активно читать куки.
    // Если gtag API уже разрешил промис, resolved будет true, и эти фолбэки не вызовут resolveOnce повторно.
    const fallbackTimeout = 100;
    setTimeout(() => {
      if (resolved) {
        // console.log("GA Client ID already resolved by gtag API, skipping cookie fallbacks.");
        return;
      }
      console.log("gtag API did not resolve quickly, attempting cookie fallbacks.");

      try {
        const cookies = typeof document !== 'undefined' ? document.cookie.split('; ') : [];
        if (cookies.length === 0 && typeof document !== 'undefined') {
            console.warn("No cookies found in document.cookie. This might be due to privacy settings or extensions.");
        }

        // 2. ФОЛБЭК: Проверка СПЕЦИФИЧНОГО GA4 cookie (приоритет перед общим _ga)
        // Убедитесь, что NEXT_PUBLIC_GA4_MEASUREMENT_ID установлен в вашем окружении!
        if (process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID) {
          const measurementIdSuffix = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID.replace(/^G-/, '');
          const ga4SpecificCookieName = `_ga_${measurementIdSuffix}`;
          const ga4SpecificCookie = cookies.find(row => row.trim().startsWith(ga4SpecificCookieName + '='));

          if (ga4SpecificCookie) {
            console.log("Found GA4 specific cookie:", ga4SpecificCookie);
            const ga4parts = ga4SpecificCookie.split('.');
            // Формат: GS1.1.CLIENT_ID.SESSION_INFO... Client ID - третья часть (индекс 2)
            if (ga4parts.length >= 3 && ga4parts[2]) {
              const clientId = ga4parts[2];
              console.log("Extracted client_id from specific GA4 cookie (_ga_XXX):", clientId);
              resolveOnce(clientId);
              return; // ID найден из специфичной GA4 куки
            } else {
              console.warn("GA4 specific cookie found but format is unexpected:", ga4SpecificCookie);
            }
          }
        } else {
            console.log("NEXT_PUBLIC_GA4_MEASUREMENT_ID not set, cannot check for specific GA4 cookie.");
        }

        // 3. ФОЛБЭК: Проверка ОБЩЕЙ _ga cookie
        if (resolved) return;

        const gaCookie = cookies.find(row => row.trim().startsWith('_ga='));
        if (gaCookie) {
          console.log("Found generic _ga cookie:", gaCookie);
          const parts = gaCookie.split('.');
          // Формат GA1.X.CLIENT_ID_PART1.CLIENT_ID_PART2. Client ID - последние две части.
          if (parts.length >= 4) {
            const clientId = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
            console.log("Extracted GA client_id from generic _ga cookie:", clientId);
            resolveOnce(clientId);
            return; // ID найден из общей _ga куки
          } else {
            console.warn("Generic _ga cookie found but format is unexpected:", gaCookie);
          }
        }
      } catch (e) {
        console.error("Error parsing cookies in fallback:", e);
      }

      // 4. ФОЛБЭК: Universal Analytics API (устаревший)
      // Проверяем только если другие методы не увенчались успехом и промис еще не разрешен
      if (!resolved && typeof window !== 'undefined' && window.ga && typeof window.ga.getAll === 'function') {
        try {
          console.log("Trying Universal Analytics API as a last resort.");
          const trackers = window.ga.getAll();
          if (trackers && trackers.length > 0) {
            const clientId = trackers[0].get('clientId');
            if (clientId && typeof clientId === 'string') {
              console.log("Got client_id from UA tracker:", clientId);
              resolveOnce(clientId);
              return; // ID получен из UA
            } else {
              console.log("UA tracker returned null or invalid client_id.");
            }
          } else {
            console.log("No UA trackers found.");
          }
        } catch (e) {
          console.error("Error with UA API:", e);
        }
      }

      // Если мы дошли сюда и промис не разрешен, значит, ни один из фолбэков не нашел ID.
      // Основной таймаут в resolveOnce(null) позаботится об этом, если он сработает раньше.
      // Если же он еще не сработал, а все фолбэки отработали без результата, можно вызвать resolveOnce(null) здесь.
      // Но для чистоты, позволим основному таймауту обработать это.
      // console.log("All fallbacks attempted, no client ID found yet. Waiting for general timeout if not already resolved.");

    }, fallbackTimeout);
  });
};

/**
 * Собирает метаданные для передачи в Stripe, включая GA client ID
 *
 * @param {string|null} [ga_client_id_param=null] - Google Analytics client ID (опционально, будет получен если не передан)
 * @param {Object} [userState=null] - Состояние пользователя (опционально)
 * @param {string} [currentPage=''] - Текущая страница (опционально)
 * @returns {Promise<Object>} - Объект с метаданными
 */
export const collectMetadata = async (ga_client_id_param = null, userState = null, currentPage = '') => {
  console.log("Collecting metadata...");
  let currentGaClientId = ga_client_id_param;
  if (!currentGaClientId) {
    console.log("No GA Client ID provided to collectMetadata, attempting to fetch...");
    currentGaClientId = await getGAClientId(); // Ждем получения Client ID
    if (currentGaClientId) {
        console.log("Fetched GA Client ID for metadata:", currentGaClientId);
    } else {
        console.log("Could not fetch GA Client ID for metadata.");
    }
  }

  let resolvedUserState = userState;
  if (!resolvedUserState && typeof window !== 'undefined' && typeof window.useUserStore?.getState === 'function') {
    try {
      resolvedUserState = window.useUserStore.getState();
      console.log("User state accessed from window.useUserStore");
    } catch (e) {
      console.error("Error accessing userStore:", e);
    }
  }

  const metadata = {
    landing_page: currentPage || (typeof window !== 'undefined' ? window.location.pathname : 'unknown'),
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
  };

  if (typeof window !== 'undefined') {
    try {
        const searchParams = new URLSearchParams(window.location.search);
        metadata.utm_source = searchParams.get('utm_source') || '';
        metadata.utm_medium = searchParams.get('utm_medium') || '';
        metadata.utm_campaign = searchParams.get('utm_campaign') || '';
    } catch(e) {
        console.error("Error parsing URL search params for UTMs:", e)
    }
  }

  if (currentGaClientId) {
    metadata.ga_client_id = currentGaClientId;
  }

  if (resolvedUserState && resolvedUserState.user) {
    metadata.user_id = resolvedUserState.user.id || '';
    metadata.user_source = resolvedUserState.user.source || 'website';
  }
  console.log("Collected metadata:", metadata);
  return metadata;
};

/**
 * Отправляет событие в Google Analytics через dataLayer
 *
 * @param {string} eventName - Название события
 * @param {Object} eventData - Данные события
 * @returns {boolean} - true, если событие отправлено успешно
 */
export const sendGAEvent = (eventName, eventData = {}) => {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn(`dataLayer not available. Cannot send GA event: ${eventName}. Event data:`, eventData);
    return false;
  }

  try {
    // Не добавляем client_id сюда напрямую, gtag сам им управляет через cookie.
    // Если нужен для GTM кастомных тегов, то можно передать в eventData
    // как отдельное свойство, например, `custom_ga_client_id`.
    const fullEventData = {
      event: eventName,
      ...eventData // eventData может содержать любые релевантные параметры для события
    };

    window.dataLayer.push(fullEventData);
    console.log(`GA event '${eventName}' pushed to dataLayer:`, fullEventData);
    return true;
  } catch (e) {
    console.error(`Error sending GA event '${eventName}':`, e, "Event data:", eventData);
    return false;
  }
};

/**
 * Полностью подготавливает и отправляет событие begin_checkout с товарами из корзины
 * и собранными метаданными.
 *
 * @param {Array<Object>} cartItems - Массив товаров в корзине. Ожидаемая структура элемента: { slug?, id?, name?, product?: { price?: number }, price?: number, quantity?: number }
 * @param {number} totalPrice - Общая стоимость корзины
 * @param {string} [currency='USD'] - Валюта
 * @returns {Promise<Object>} - Объект с метаданными, статусом отправки события и GA Client ID
 */
export const prepareCheckoutEvent = async (cartItems, totalPrice, currency = 'USD') => {
  console.log("Preparing checkout event...");
  const gaClientId = await getGAClientId(); // Получаем Client ID
  if (gaClientId) {
    console.log("GA Client ID for checkout event:", gaClientId);
  } else {
    console.log("Could not obtain GA Client ID for checkout event.");
  }


  const items = (Array.isArray(cartItems) ? cartItems : []).map(item => ({
    item_id: item.slug || (item.id ? String(item.id) : undefined),
    item_name: item.name || item.slug || undefined,
    price: (item.product && typeof item.product.price === 'number') ? item.product.price : (typeof item.price === 'number' ? item.price : undefined),
    quantity: typeof item.quantity === 'number' ? item.quantity : 1,
  })).filter(item => item.item_id && item.item_name && typeof item.price === 'number'); // Оставляем только валидные товары

  if (items.length !== (Array.isArray(cartItems) ? cartItems.length : 0)) {
    console.warn("Some cart items were filtered out due to missing id, name, or price during checkout event preparation.");
  }

  const eventData = {
    currency: currency,
    value: totalPrice,
    items: items,
  };

  // Если вы хотите явно передать client_id для каких-то своих целей (например, для GTM или отладки),
  // то можно добавить его как кастомный параметр.
  // Для стандартной работы GA4 через gtag.js это обычно не требуется, т.к. gtag сам его подхватывает из cookie.
  if (gaClientId) {
    eventData.custom_ga_client_id = gaClientId; // Пример кастомного параметра для отладки или специфических нужд GTM
  }

  const eventSent = sendGAEvent('begin_checkout', eventData);

  const metadata = await collectMetadata(gaClientId); // Передаем уже полученный ID, чтобы не запрашивать снова

  const result = {
    metadata,
    eventSent,
    ga_client_id: gaClientId // Возвращаем для возможного использования или отладки
  };
  console.log("Checkout event preparation complete:", result);
  return result;
};

// Экспортируем объект со всеми утилитами
const analyticsUtils = {
  getGAClientId,
  collectMetadata,
  sendGAEvent,
  prepareCheckoutEvent
};

export default analyticsUtils;